import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  ClipboardCheck, Plus, Pencil, Trash2, History, Store, ChevronUp, ChevronDown,
  Ban, ShieldAlert, Zap, CalendarClock, CheckCircle2, X,
} from "lucide-react";
import api from "../lib/api";
import { Card, Badge, Button, PageHeader, PageLoader, Modal } from "../lib/ui";
import { useToast } from "../lib/toast";
import { useAuth } from "../context/AuthContext";

/* Kitchen SOP — Owner configuration (Phase 2B).
   Global Template (versioned, effective date) + Branch Override (Model 1C:
   enable/disable global items + branch-specific additions only). */

const SEV_CLS = { info: "bg-blue-50 text-blue-700", warning: "bg-amber-50 text-amber-700", critical: "bg-red-50 text-red-700" };
const BLOCK_LABEL = { never: "Never Block", warn: "Warn Only", block: "Block Close Session" };

const emptyItem = () => ({
  label: "", section: "", check_type: "checkbox", severity: "info", block: "never",
  require_note: "optional", require_photo: "disabled", validation: {}, options: [], auto_rule: "",
});

/* ------------------------------- item form ------------------------------- */
function ItemForm({ meta, value, onSave, onCancel }) {
  const [f, setF] = useState(value || emptyItem());
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const setV = (k, v) => setF((s) => ({ ...s, validation: { ...(s.validation || {}), [k]: v } }));
  const numeric = ["number", "temperature", "weight"].includes(f.check_type);
  const sel = "w-full rounded-lg border border-line bg-white px-2 py-1.5 text-xs focus:border-primary focus:outline-none";
  const inp = "w-full rounded-lg border border-line px-2 py-1.5 text-xs focus:border-primary focus:outline-none";
  const L = ({ children }) => <span className="mb-0.5 block text-[10px] font-bold uppercase text-ink-mute">{children}</span>;
  return (
    <div className="space-y-2.5 rounded-xl border border-primary/30 bg-primary-light/20 p-3" data-testid="sop-item-form">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <label><L>Nama Item *</L>
          <input value={f.label} onChange={(e) => set("label", e.target.value)} className={inp} data-testid="sop-if-label" placeholder="cth: Suhu freezer utama" /></label>
        <label><L>Section / Station</L>
          <input value={f.section || ""} onChange={(e) => set("section", e.target.value)} className={inp} data-testid="sop-if-section" placeholder="cth: Freezer, Wok, Prep Table" list="sop-sections" />
          <datalist id="sop-sections">{["Prep Table", "Wok", "BBQ", "Sushi Corner", "Beverage", "Storage", "Freezer", "Chiller"].map((s) => <option key={s} value={s} />)}</datalist>
        </label>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <label><L>Tipe Check</L>
          <select value={f.check_type} onChange={(e) => set("check_type", e.target.value)} className={sel} data-testid="sop-if-type">
            {meta.check_types.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select></label>
        <label><L>Severity</L>
          <select value={f.severity} onChange={(e) => set("severity", e.target.value)} className={sel} data-testid="sop-if-severity">
            {meta.severities.map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
          </select></label>
        <label><L>Block Behavior</L>
          <select value={f.block} onChange={(e) => set("block", e.target.value)} className={sel} data-testid="sop-if-block">
            {meta.block_behaviors.map((b) => <option key={b} value={b}>{BLOCK_LABEL[b]}</option>)}
          </select></label>
        <label><L>Validasi Otomatis</L>
          <select value={f.auto_rule || ""} onChange={(e) => set("auto_rule", e.target.value)} className={sel} data-testid="sop-if-auto">
            <option value="">— Manual (inspeksi fisik) —</option>
            {meta.auto_rules.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
          </select></label>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <label><L>Catatan</L>
          <select value={f.require_note} onChange={(e) => set("require_note", e.target.value)} className={sel} data-testid="sop-if-note">
            {meta.req_modes.map((m) => <option key={m} value={m}>{m === "disabled" ? "Nonaktif" : m === "optional" ? "Opsional" : "Wajib"}</option>)}
          </select></label>
        <label><L>Foto</L>
          <select value={f.require_photo} onChange={(e) => set("require_photo", e.target.value)} className={sel} data-testid="sop-if-photo">
            {meta.req_modes.map((m) => <option key={m} value={m}>{m === "disabled" ? "Nonaktif" : m === "optional" ? "Opsional" : "Wajib"}</option>)}
          </select></label>
        {numeric && (<>
          <label><L>Min</L>
            <input type="number" step="0.1" value={f.validation?.min ?? ""} onChange={(e) => setV("min", e.target.value)} className={inp} data-testid="sop-if-min" placeholder="—" /></label>
          <label><L>Max {f.check_type === "temperature" ? "(°C)" : ""}</L>
            <input type="number" step="0.1" value={f.validation?.max ?? ""} onChange={(e) => setV("max", e.target.value)} className={inp} data-testid="sop-if-max" placeholder="—" /></label>
        </>)}
      </div>
      {f.check_type === "dropdown" && (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <label><L>Opsi (pisahkan dengan koma) *</L>
            <input value={(f.options || []).join(", ")} onChange={(e) => set("options", e.target.value.split(",").map((s) => s.trim()))} className={inp} data-testid="sop-if-options" placeholder="Bersih, Kotor, Perlu Servis" /></label>
          <label><L>Jawaban Lulus (kosongkan = semua lulus)</L>
            <input value={((f.validation || {}).expected || []).join(", ")} onChange={(e) => setV("expected", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} className={inp} data-testid="sop-if-expected" placeholder="Bersih" /></label>
        </div>
      )}
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="outline" onClick={onCancel} data-testid="sop-if-cancel"><X size={13} /> Batal</Button>
        <Button size="sm" disabled={!f.label.trim()} onClick={() => onSave(f)} data-testid="sop-if-save"><CheckCircle2 size={13} /> Simpan Item</Button>
      </div>
    </div>
  );
}

/* ------------------------------- item row (readonly) ------------------------------- */
function ItemChipRow({ it, onEdit, onDelete, onMove, idx, count, extraBadge }) {
  return (
    <div className="flex items-center gap-2 rounded-xl border border-line bg-white p-2.5" data-testid={`sop-cfg-item-${idx}`}>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <p className="text-sm font-bold text-ink">{it.label}</p>
          {it.section && <span className="rounded-full bg-stone-100 px-1.5 py-0.5 text-[9px] font-bold text-ink-soft">{it.section}</span>}
          <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-extrabold ${SEV_CLS[it.severity]}`}>{it.severity.toUpperCase()}</span>
          {it.block === "block" && <span className="inline-flex items-center gap-0.5 rounded-full bg-red-100 px-1.5 py-0.5 text-[9px] font-extrabold text-red-700"><Ban size={9} /> BLOCK</span>}
          {it.block === "warn" && <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-extrabold text-amber-700"><ShieldAlert size={9} /> WARN</span>}
          {it.auto_rule && <span className="inline-flex items-center gap-0.5 rounded-full bg-violet-100 px-1.5 py-0.5 text-[9px] font-extrabold text-violet-700"><Zap size={9} /> AUTO</span>}
          {extraBadge}
        </div>
        <p className="text-[10px] text-ink-mute">
          {it.check_type}{it.validation?.min != null || it.validation?.max != null ? ` · ${it.validation?.min ?? ""}…${it.validation?.max ?? ""}` : ""}
          {it.require_photo === "required" && " · foto wajib"}{it.require_note === "required" && " · catatan wajib"}
        </p>
      </div>
      {onMove && (
        <div className="flex flex-col">
          <button disabled={idx === 0} onClick={() => onMove(idx, -1)} className="text-ink-mute disabled:opacity-20"><ChevronUp size={14} /></button>
          <button disabled={idx === count - 1} onClick={() => onMove(idx, 1)} className="text-ink-mute disabled:opacity-20"><ChevronDown size={14} /></button>
        </div>
      )}
      {onEdit && <button onClick={onEdit} className="flex h-7 w-7 items-center justify-center rounded-lg border border-line text-ink-mute hover:border-primary hover:text-primary" data-testid={`sop-cfg-edit-${idx}`}><Pencil size={12} /></button>}
      {onDelete && <button onClick={onDelete} className="flex h-7 w-7 items-center justify-center rounded-lg border border-line text-ink-mute hover:border-danger hover:text-danger-text" data-testid={`sop-cfg-del-${idx}`}><Trash2 size={12} /></button>}
    </div>
  );
}

/* ------------------------------- template editor modal ------------------------------- */
function TemplateModal({ meta, tpl, onClose, onSaved }) {
  const toast = useToast();
  const isNew = !tpl;
  const [name, setName] = useState(tpl?.name || "");
  const [category, setCategory] = useState(tpl?.category || "opening");
  const [station, setStation] = useState(tpl?.station || "kitchen");
  const [items, setItems] = useState(tpl?.items || []);
  const [editIdx, setEditIdx] = useState(null); // null | -1 (new) | index
  const [publishMode, setPublishMode] = useState("now");
  const [effDate, setEffDate] = useState("");
  const [busy, setBusy] = useState(false);

  const move = (i, d) => setItems((a) => { const n = [...a]; const [x] = n.splice(i, 1); n.splice(i + d, 0, x); return n; });
  const saveItem = (f) => {
    setItems((a) => (editIdx === -1 ? [...a, f] : a.map((x, i) => (i === editIdx ? { ...x, ...f } : x))));
    setEditIdx(null);
  };
  const save = async () => {
    setBusy(true);
    try {
      const payload = { name, category, station, items, effective_from: publishMode === "schedule" ? effDate : undefined };
      if (isNew) await api.post("/prepare/sop/templates", payload);
      else await api.put(`/prepare/sop/templates/${tpl.template_key}`, payload);
      toast.success(publishMode === "schedule" ? `Terjadwal aktif ${effDate}` : "Template diterbitkan");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title={isNew ? "Template SOP Baru" : `Edit: ${tpl.name} (v${tpl.version})`} size="lg"
      footer={<div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs">
          <select value={publishMode} onChange={(e) => setPublishMode(e.target.value)} data-testid="sop-tpl-publishmode"
            className="rounded-lg border border-line bg-white px-2 py-1.5 text-xs focus:outline-none">
            <option value="now">Terbitkan Sekarang</option>
            <option value="schedule">Jadwalkan Publish</option>
          </select>
          {publishMode === "schedule" && <input type="date" value={effDate} onChange={(e) => setEffDate(e.target.value)} data-testid="sop-tpl-effdate" className="rounded-lg border border-line px-2 py-1.5 text-xs focus:outline-none" />}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={save} loading={busy} disabled={!name.trim() || (publishMode === "schedule" && !effDate)} data-testid="sop-tpl-save"><CheckCircle2 size={14} /> Simpan Template</Button>
        </div>
      </div>}>
      <div className="space-y-3" data-testid="sop-tpl-modal">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <label className="sm:col-span-1"><span className="mb-0.5 block text-[10px] font-bold uppercase text-ink-mute">Nama Template *</span>
            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="sop-tpl-name" className="w-full rounded-lg border border-line px-2 py-1.5 text-sm focus:border-primary focus:outline-none" placeholder="cth: Opening Checklist Dapur" /></label>
          <label><span className="mb-0.5 block text-[10px] font-bold uppercase text-ink-mute">Kategori</span>
            <select value={category} disabled={!isNew} onChange={(e) => setCategory(e.target.value)} data-testid="sop-tpl-category" className="w-full rounded-lg border border-line bg-white px-2 py-1.5 text-sm focus:outline-none disabled:opacity-60">
              {meta.categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select></label>
          <label><span className="mb-0.5 block text-[10px] font-bold uppercase text-ink-mute">Station</span>
            <select value={station} disabled={!isNew} onChange={(e) => setStation(e.target.value)} data-testid="sop-tpl-station" className="w-full rounded-lg border border-line bg-white px-2 py-1.5 text-sm focus:outline-none disabled:opacity-60">
              {meta.stations.map((s) => <option key={s} value={s}>{s === "kitchen" ? "Kitchen" : "Beverage"}</option>)}
            </select></label>
        </div>
        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <p className="text-xs font-extrabold uppercase tracking-wide text-ink-soft">Item SOP ({items.length})</p>
            {editIdx === null && <button onClick={() => setEditIdx(-1)} className="inline-flex items-center gap-1 text-xs font-bold text-primary" data-testid="sop-tpl-additem"><Plus size={13} /> Tambah Item</button>}
          </div>
          <div className="space-y-1.5">
            {items.map((it, i) => editIdx === i
              ? <ItemForm key={i} meta={meta} value={it} onSave={saveItem} onCancel={() => setEditIdx(null)} />
              : <ItemChipRow key={i} it={it} idx={i} count={items.length}
                  onEdit={() => setEditIdx(i)} onDelete={() => setItems((a) => a.filter((_, j) => j !== i))} onMove={move} />)}
            {editIdx === -1 && <ItemForm meta={meta} onSave={saveItem} onCancel={() => setEditIdx(null)} />}
            {items.length === 0 && editIdx === null && <p className="py-4 text-center text-xs text-ink-mute">Belum ada item. Klik “Tambah Item”.</p>}
          </div>
        </div>
      </div>
    </Modal>
  );
}

/* ------------------------------- branch override modal ------------------------------- */
function OverrideModal({ meta, tpl, onClose }) {
  const toast = useToast();
  const [target, setTarget] = useState(meta.branch_targets[0]?.key || "central");
  const [ov, setOv] = useState(null);
  const [editIdx, setEditIdx] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/prepare/sop/templates/${tpl.template_key}/overrides`);
    const found = (data.overrides || []).find((o) => o.branch_key === target);
    setOv(found || { branch_key: target, disabled_item_ids: [], extra_items: [] });
    setEditIdx(null);
  }, [tpl.template_key, target]);
  useEffect(() => { load(); }, [load]);

  const save = async (next) => {
    setBusy(true);
    try {
      await api.put(`/prepare/sop/templates/${tpl.template_key}/branch/${target}`, {
        disabled_item_ids: next.disabled_item_ids, extra_items: next.extra_items,
      });
      setOv(next);
      toast.success("Override cabang tersimpan");
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); } finally { setBusy(false); }
  };
  if (!ov) return null;
  const disabled = new Set(ov.disabled_item_ids || []);
  const toggle = (id) => {
    const next = new Set(disabled);
    next.has(id) ? next.delete(id) : next.add(id);
    save({ ...ov, disabled_item_ids: [...next] });
  };
  return (
    <Modal open onClose={onClose} title={`Override Cabang — ${tpl.name}`} size="lg"
      footer={<div className="flex justify-end"><Button variant="outline" onClick={onClose}>Tutup</Button></div>}>
      <div className="space-y-3" data-testid="sop-ov-modal">
        <label className="block"><span className="mb-0.5 block text-[10px] font-bold uppercase text-ink-mute">Target</span>
          <select value={target} onChange={(e) => setTarget(e.target.value)} data-testid="sop-ov-target" className="w-full max-w-xs rounded-lg border border-line bg-white px-2 py-1.5 text-sm focus:outline-none">
            {meta.branch_targets.map((b) => <option key={b.key} value={b.key}>{b.name}</option>)}
          </select></label>
        <div className="rounded-xl bg-sky-50 px-3 py-2 text-[11px] text-sky-900">
          Item Global hanya bisa di-<b>aktif/nonaktifkan</b> per cabang. Edit item Global dilakukan di Template Global. Cabang boleh <b>menambah item sendiri</b>.
        </div>
        <div>
          <p className="mb-1.5 text-xs font-extrabold uppercase tracking-wide text-ink-soft">Item Global</p>
          <div className="space-y-1.5">
            {(tpl.items || []).map((it) => {
              const off = disabled.has(it.item_id);
              return (
                <div key={it.item_id} className={`flex items-center gap-2 rounded-xl border p-2.5 ${off ? "border-line bg-stone-50 opacity-60" : "border-line bg-white"}`} data-testid={`sop-ov-g-${it.item_id}`}>
                  <button type="button" disabled={busy} onClick={() => toggle(it.item_id)} data-testid={`sop-ov-toggle-${it.item_id}`}
                    className={`relative h-5 w-9 shrink-0 rounded-full transition ${off ? "bg-stone-300" : "bg-emerald-500"}`}>
                    <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${off ? "left-0.5" : "left-[18px]"}`} />
                  </button>
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm font-bold ${off ? "text-ink-mute line-through" : "text-ink"}`}>{it.label}</p>
                    <p className="text-[10px] text-ink-mute">{it.section || "—"} · {it.check_type} · {it.severity.toUpperCase()}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <p className="text-xs font-extrabold uppercase tracking-wide text-ink-soft">Item Khusus Cabang ({(ov.extra_items || []).length})</p>
            {editIdx === null && <button onClick={() => setEditIdx(-1)} className="inline-flex items-center gap-1 text-xs font-bold text-primary" data-testid="sop-ov-additem"><Plus size={13} /> Tambah Item Cabang</button>}
          </div>
          <div className="space-y-1.5">
            {(ov.extra_items || []).map((it, i) => editIdx === i
              ? <ItemForm key={i} meta={meta} value={it} onSave={(f) => { save({ ...ov, extra_items: ov.extra_items.map((x, j) => (j === i ? { ...x, ...f } : x)) }); setEditIdx(null); }} onCancel={() => setEditIdx(null)} />
              : <ItemChipRow key={i} it={it} idx={i} count={ov.extra_items.length}
                  extraBadge={<span className="rounded-full bg-sky-100 px-1.5 py-0.5 text-[9px] font-extrabold text-sky-700">CABANG</span>}
                  onEdit={() => setEditIdx(i)} onDelete={() => save({ ...ov, extra_items: ov.extra_items.filter((_, j) => j !== i) })} />)}
            {editIdx === -1 && <ItemForm meta={meta} onSave={(f) => { save({ ...ov, extra_items: [...(ov.extra_items || []), f] }); setEditIdx(null); }} onCancel={() => setEditIdx(null)} />}
          </div>
        </div>
      </div>
    </Modal>
  );
}

/* ------------------------------- version history modal ------------------------------- */
function VersionsModal({ tpl, onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get(`/prepare/sop/templates/${tpl.template_key}/versions`).then(({ data }) => setRows(data.versions || [])).catch(() => setRows([]));
  }, [tpl.template_key]);
  return (
    <Modal open onClose={onClose} title={`Riwayat Versi — ${tpl.name}`} size="md"
      footer={<div className="flex justify-end"><Button variant="outline" onClick={onClose}>Tutup</Button></div>}>
      {!rows ? <PageLoader /> : (
        <div className="space-y-1.5" data-testid="sop-versions">
          {rows.map((v) => (
            <div key={v.id} className="flex items-center gap-2 rounded-xl border border-line p-2.5">
              <Badge tone={v.status === "active" ? "success" : v.status === "scheduled" ? "warn" : "mute"}>v{v.version}</Badge>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-ink">{v.name} <span className="text-[10px] font-normal text-ink-mute">· {v.status}</span></p>
                <p className="text-[10px] text-ink-mute">Efektif {v.effective_from} · {v.published_by_name} · {(v.items || []).length} item · {v.submission_count} submission</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

/* ================================= main page ================================= */
export default function KitchenSOP() {
  const toast = useToast();
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [data, setData] = useState(null);
  const [cat, setCat] = useState("opening");
  const [modal, setModal] = useState(null); // {type: 'edit'|'new'|'override'|'versions', tpl}

  const load = useCallback(async () => {
    const [{ data: m }, { data: t }] = await Promise.all([
      api.get("/prepare/sop/meta"), api.get("/prepare/sop/templates"),
    ]);
    setMeta(m); setData(t);
  }, []);
  useEffect(() => { load(); }, [load]);

  const archive = async (tpl) => {
    if (!window.confirm(`Arsipkan template "${tpl.name}"? Riwayat submission tetap tersimpan.`)) return;
    try { await api.post(`/prepare/sop/templates/${tpl.template_key}/archive`); toast.success("Diarsipkan"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  // Owner-only page (per Phase 2B contract) — Head Chef must never see SOP config.
  if (user && !["OWNER", "SUPER_ADMIN"].includes(user.role)) return <Navigate to="/" replace />;
  if (!meta || !data) return <PageLoader />;
  const tpls = (data.templates || []).filter((t) => t.category === cat);

  return (
    <div className="animate-fade-up pb-24 lg:pb-2" data-testid="kitchen-sop-page">
      <PageHeader icon={ClipboardCheck} title="Kitchen SOP" subtitle="Standar operasional dapur — Template Global dengan Override per Cabang. Head Chef mengeksekusi di Meja Prepare.">
        <Button onClick={() => setModal({ type: "new" })} data-testid="sop-new-template-btn"><Plus size={15} /> Template Baru</Button>
      </PageHeader>

      <div className="mb-4 flex flex-wrap gap-2" data-testid="sop-category-tabs">
        {meta.categories.map((c) => {
          const n = (data.templates || []).filter((t) => t.category === c.key).length;
          return (
            <button key={c.key} onClick={() => setCat(c.key)} data-testid={`sop-tab-${c.key}`}
              className={`rounded-full border px-4 py-1.5 text-sm font-bold transition ${cat === c.key ? "border-primary bg-primary text-white" : "border-line bg-white text-ink-soft hover:bg-stone-50"}`}>
              {c.label} {n > 0 && <span className="opacity-70">({n})</span>}
            </button>
          );
        })}
      </div>

      {tpls.length === 0 ? (
        <Card className="p-10 text-center" data-testid="sop-empty">
          <ClipboardCheck size={32} className="mx-auto mb-2 text-ink-mute" />
          <p className="text-sm text-ink-mute">Belum ada template {meta.categories.find((c) => c.key === cat)?.label}. Klik <b>Template Baru</b> untuk membuat.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {tpls.map((t) => (
            <Card key={t.template_key} className="p-4" data-testid={`sop-tplcard-${t.template_key}`}>
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <h3 className="font-heading text-base font-extrabold text-ink">{t.name}</h3>
                <Badge tone="mute">v{t.version}</Badge>
                <Badge tone={t.station === "beverage" ? "warn" : "success"}>{t.station === "beverage" ? "Beverage" : "Kitchen"}</Badge>
                {t.override_count > 0 && <Badge tone="warn"><Store size={10} /> {t.override_count} override</Badge>}
                {t.scheduled_version && <Badge tone="warn"><CalendarClock size={10} /> v{t.scheduled_version.version} aktif {t.scheduled_version.effective_from}</Badge>}
              </div>
              <p className="mb-2 text-[11px] text-ink-mute">Efektif {t.effective_from} · {t.published_by_name} · {(t.items || []).length} item</p>
              <div className="mb-3 flex flex-wrap gap-1">
                {(t.items || []).slice(0, 6).map((it) => (
                  <span key={it.item_id} className="rounded-full bg-app px-2 py-0.5 text-[10px] font-semibold text-ink-soft">
                    {it.auto_rule && "⚡"}{it.block === "block" && "⛔"}{it.label}
                  </span>
                ))}
                {(t.items || []).length > 6 && <span className="text-[10px] text-ink-mute">+{t.items.length - 6} lainnya</span>}
              </div>
              <div className="flex flex-wrap gap-1.5">
                <Button size="sm" variant="outline" onClick={() => setModal({ type: "edit", tpl: t })} data-testid={`sop-edit-${t.template_key}`}><Pencil size={12} /> Edit</Button>
                <Button size="sm" variant="outline" onClick={() => setModal({ type: "override", tpl: t })} data-testid={`sop-override-${t.template_key}`}><Store size={12} /> Override Cabang</Button>
                <Button size="sm" variant="outline" onClick={() => setModal({ type: "versions", tpl: t })} data-testid={`sop-versions-${t.template_key}`}><History size={12} /> Riwayat</Button>
                <Button size="sm" variant="outline" onClick={() => archive(t)} data-testid={`sop-archive-${t.template_key}`}><Trash2 size={12} /> Arsip</Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {modal?.type === "new" && <TemplateModal meta={meta} tpl={null} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {modal?.type === "edit" && <TemplateModal meta={meta} tpl={modal.tpl} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {modal?.type === "override" && <OverrideModal meta={meta} tpl={modal.tpl} onClose={() => { setModal(null); load(); }} />}
      {modal?.type === "versions" && <VersionsModal tpl={modal.tpl} onClose={() => setModal(null)} />}
    </div>
  );
}
